import { createFileRoute } from '@tanstack/react-router'
import { useRegionGuide, regionGuideQueryOptions } from '~/features/guides/hooks/use-region-guide'
import { GuideViewer } from '~/components/common/GuideViewer'
import { seo } from '~/utils/seo'
import { extractGuideTitle } from '~/utils/guide-title'
import { EDGE_CACHE_HEADERS } from '~/utils/cache'
import type { QueryClient } from '@tanstack/react-query'
import { Spinner } from '@heroui/react'
import { defaultLocale, localeToLanguageCode } from '~/lib/i18n/shared'

function getFallbackSeoText(locale: string) {
  switch (locale) {
    case 'zh-CN': return { title: '地区文化指南 | TabeTabi', description: '探索日本各地饮食文化与禁忌。' }
    case 'zh-TW': return { title: '地區文化指南 | TabeTabi', description: '探索日本各地飲食文化與禁忌。' }
    case 'ja': return { title: '地域文化ガイド | TabeTabi', description: '日本の各地域の食文化とタブーを探索しましょう。' }
    case 'ko': return { title: '지역 문화 가이드 | TabeTabi', description: '일본 각 지역의 음식 문화와 금기를 탐색해보세요.' }
    default: return { title: 'Regional Culture Guide | TabeTabi', description: 'Explore local food culture and taboos in Japan.' }
  }
}

export const Route = createFileRoute('/{-$locale}/_public/regions/$regionId')({
  loader: async ({ context, params }) => {
    const queryClient = context.queryClient as QueryClient
    // Extract lang code, falling back to defaultLocale
    const rawLocale = (params as any).locale || defaultLocale
    const lang = localeToLanguageCode(rawLocale)
    
    try {
      const guide = await queryClient.ensureQueryData(regionGuideQueryOptions(params.regionId, lang))
      return { guide, lang }
    } catch (e) {
      return { guide: null, lang }
    }
  },
  head: ({ loaderData, params }) => {
    const locale = (params as any).locale || defaultLocale
    const fallback = getFallbackSeoText(locale)
    
    const guide = loaderData?.guide
    const title = guide?.contentMd
      ? extractGuideTitle(guide.contentMd, fallback.title)
      : fallback.title

    const { meta, links } = seo({
      title,
      description: fallback.description,
      pathname: `/regions/${params.regionId}`,
      locale,
    })

    return {
      meta,
      links,
    }
  },
  headers: () => EDGE_CACHE_HEADERS,
  component: RegionGuidePage,
})

function RegionGuidePage() {
  const { regionId } = Route.useParams()
  const { lang } = Route.useLoaderData()
  const { data: guide, isLoading } = useRegionGuide(regionId, lang)

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!guide || !guide.contentMd) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Guide Not Found</h1>
        <p className="mt-4 text-default-500">
          We could not find the guide for this region in your language.
        </p>
      </div>
    )
  }

  return (
    <main className="container mx-auto px-4 py-8">
      <GuideViewer contentMd={guide.contentMd} />
    </main>
  )
}
